import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import {
  CashRegister,
  GetCashRegisterCatalogsQuery,
} from 'src/app/graphql/types';
import { BaseDialogComponent } from 'src/app/main/pages/generals/dialog/base-dialog/base-dialog.component';
import { LoadingComponent } from 'src/app/main/pages/generals/loading/loading.component';
import { LoadingModule } from 'src/app/main/pages/generals/loading/loading.module';
import { ActivityService } from 'src/app/main/pages/services/activity.service';
import { CashRegisterService } from 'src/app/main/pages/services/cash-register.service';
import { IdDescriptionJson } from 'src/app/main/pages/services/microservices/json/id-description.json';
import { NumerosUtils } from 'src/app/shared';
import { SharedService } from 'src/app/shared/shared.service';
import { FormValidator } from 'src/app/shared/utils/form-validator';

@Component({
  selector: 'app-cash-register-dialog',
  templateUrl: './cash-register-dialog.component.html',
  styleUrls: ['./cash-register-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    LoadingModule,
    BaseDialogComponent,
    LoadingComponent,
  ],
})
export class CashRegisterDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<CashRegisterDialogComponent>);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private service = inject(CashRegisterService);
  private activityService = inject(ActivityService);
  private sharedService = inject(SharedService);
  private _snackBar = inject(MatSnackBar);
  private _unsubscribeAll!: Subject<any>;

  public data: { cashRegisterId: number; businessUnitId: number } =
    inject(MAT_DIALOG_DATA);
  isLoading = false;
  notification: any = {};
  form!: FormGroup;
  cashRegCatalogs!: GetCashRegisterCatalogsQuery | null;
  activList!: Array<IdDescriptionJson>;
  txtBtn = 'AGREGAR';

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.formInit();
    this.load();
    this.loadCatalogs();
  }

  private load(): void {
    if (this.data.cashRegisterId > 0) {
      this.service.get(this.data.cashRegisterId).subscribe(data => {
        if (data) {
          this.loadInForm(data);
        }
        this.isLoading = false;
        this.txtBtn = 'ACTUALIZAR';
      });
    } else {
      this.loadCashRegisterNumber();
      this.form.patchValue({
        businessUnitId: this.data.businessUnitId,
      });
    }
  }

  private loadCashRegisterNumber(): void {
    this.service
      .getCashRegisterNumber(this.data.businessUnitId)
      .subscribe(({ number }) => {
        const numberStr = NumerosUtils.addZero(Number(number));
        this.form.patchValue({ number: numberStr });
      });
  }

  private loadCatalogs(): void {
    this.service.getCashRegisterCatalogs().subscribe(data => {
      this.cashRegCatalogs = data;
    });
    this.sharedService.taxpayerId$.subscribe(taxId => {
      if (taxId > 0) {
        this.getEconomicActivities(taxId);
      }
    });
  }

  private getEconomicActivities(taxpayerId: number) {
    this.activityService.getIdDescription(taxpayerId).subscribe({
      next: response => {
        if (response.data) {
          this.activList = response.data;
        }
      },
    });
  }

  submit(): void {
    this.isLoading = true;
    this.form.patchValue({
      id: this.data.cashRegisterId,
    });
    let serviceAction;
    if (this.form.get('id')?.value === 0) {
      serviceAction = this.service.save(this.form);
    } else {
      serviceAction = this.service.update(this.form);
    }
    serviceAction.subscribe(data => {
      if (data) {
        this.isLoading = false;
        this.dialogRef.close(data.id);
      }
    });
  }

  //Initializers
  private formInit(): void {
    this.form = this.fb.group(
      {
        id: [0],
        businessUnitId: [0],
        name: ['', Validators.required],
        number: [{ value: '0', disabled: true }],
        sriEnvironment: ['', Validators.required],
        type: ['', Validators.required],
        cashRegisterUser: [1],
        economicActivity: ['', Validators.required],
        active: true,
      },
      { updateOn: 'blur' },
    );
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    const fieldsNoti = {
      name: { required: 'Nombre es requerido' },
      sriEnvironment: { required: 'Ambiente es requerido' },
      type: { required: 'Tipo caja es requerido' },
      economicActivity: { required: 'Actividad económica requerida' },
    };
    this.fv.setFormValidator(this.form, this.notification, fieldsNoti);
  }

  private loadInForm(json: CashRegister) {
    const number = NumerosUtils.addZero(json.number!);
    this.form.patchValue({
      id: json!.id,
      businessUnitId: json!.businessUnitId!,
      name: json!.name,
      number,
      sriEnvironment: json!.sriEnvironment?.id,
      type: json!.type!.id,
      cashRegisterUser: Number(json!.user?.id),
      activity: Number(json!.activity!.id),
      active: json!.active!,
    });
  }

  //Auxiliar methods
  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }
}
